import React, { forwardRef, useImperativeHandle, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DeleteIcon from '../../static/image/deleteIcon.svg';
import { setValueHardware } from '../../redux/productSlice';
import CustomInput from '../../components/customInput/CustomInput';
import ModalFileUpload from '../../components/ModalFileUpload/ModalFileUpload';
import CommonDeleteModal from '../../components/commonDeleteModal/CommonDeleteModal';
import { uploadAsset, deleteAsset } from '../../redux/assetSlice';
import { ASSET_LOCATION_MAPPING, ASSET_TYPE_MAPPING, LABEL_DELETE_ASSET } from '../../utils/constant';
import { isEmpty } from '../../utils/commonUtil';

const Hardware = forwardRef(({ language, productId, formType, onShowAlert }, ref) => {
  const dispatch = useDispatch();
  const productState = useSelector((state) => state.product);
  const product = productState.data || {};

  const hardwareData = product.hardware || product.contentData?.hardware || {};
  const hardwareItems = hardwareData.items || [];
  const hardwareTitle = hardwareData.title || '';

  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [isShowDeleteAsset, setIsShowDeleteAsset] = useState(null);
  const [selectedHardwareIndex, setSelectedHardwareIndex] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const [showDeleteHardwareModal, setShowDeleteHardwareModal] = useState(false);
  const [hardwareToDelete, setHardwareToDelete] = useState(null);

  const PLACEHOLDER_CONFIG = {
    title: {
      EN: "Enter hardware title EN",
      CN: "Enter hardware title CN"
    },
    hardwareTitle: {
      EN: "Enter hardware item title EN",
      CN: "Enter hardware item title CN"
    },
    hardwareDescription: {
      EN: "Enter hardware item description EN",
      CN: "Enter hardware item description CN"
    },
    imageUrl: {
      EN: "image.png EN",
      CN: "image.png CN"
    },
    retainHardware: {
      EN: "At least retain one hardware item EN",
      CN: "At least retain one hardware item CN"
    }
  };

  const INITIAL_HARDWARE_ITEM = {
    title: '',
    desc: '',
    imageUrl: '',
    imageFileId: '',
    width: 275,
    height: 275
  };

  useEffect(() => {
    if (!hardwareData.title && hardwareItems.length === 0) {
      dispatch(setValueHardware({
        value: {
          title: '',
          items: [INITIAL_HARDWARE_ITEM]
        }
      }));
    }
  }, [hardwareData, hardwareItems.length, dispatch]);

  const handleFileUpload = (file) => {
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("uuid", formType === 'EDIT' ? productId : "");
    formData.append("assetLocation", ASSET_LOCATION_MAPPING[selectedField]);
    formData.append("assetType", ASSET_TYPE_MAPPING.product);
    formData.append("file", file);

    dispatch(uploadAsset(formData))
      .then((res) => {
        if (res?.payload) {
          const fileId = res.payload.resdata.fileId;
          const fileUrl = res.payload.resdata.url;

          dispatch((dispatch, getState) => {
            const state = getState();
            const product = state.product.data || {};
            const hardwareData = product.hardware || product.contentData?.hardware || {};
            const currentHardwareItems = hardwareData.items || [];

            const newItems = currentHardwareItems.map((item, i) => {
              if (i === selectedHardwareIndex) {
                return {
                  ...item,
                  imageUrl: fileUrl,
                  imageFileId: fileId
                };
              }
              return item;
            });

            dispatch(setValueHardware({
              value: {
                ...hardwareData,
                items: newItems
              }
            }));
          });
        }
        if (res?.error) {
          onShowAlert(res.error.message, false);
        }
      })
      .catch((err) => {
        onShowAlert(err.message || "Upload failed", false);
      })
      .finally(() => {
        setIsUploading(false);
        setShowFileUploadModal(false);
        setSelectedHardwareIndex(null);
        setSelectedField(null);
      });
  };

  const handleDeleteFile = () => {
    const fileId = hardwareItems[selectedHardwareIndex]?.imageFileId;

    const newItems = hardwareItems.map((item, i) => {
      if (i === selectedHardwareIndex) {
        return {
          ...item,
          imageUrl: "",
          imageFileId: ""
        };
      }
      return item;
    });

    dispatch(setValueHardware({
      value: {
        ...hardwareData,
        items: newItems
      }
    }));

    if (fileId) {
      dispatch(deleteAsset(fileId))
        .then((res) => {
          if (res?.error &&
            !res.error.message.includes("No active document") &&
            !res.error.message.includes("Unexpected token")) {
            onShowAlert(res.error.message, false);
          }
        })
        .catch((err) => {
          if (err.message &&
            !err.message.includes("No active document") &&
            !err.message.includes("Unexpected token")) {
            onShowAlert(err.message, false);
          }
        })
        .finally(() => {
          setIsShowDeleteAsset(null);
          setSelectedHardwareIndex(null);
          setSelectedField(null);
        });
    } else {
      setIsShowDeleteAsset(null);
      setSelectedHardwareIndex(null);
      setSelectedField(null);
    }
  };

  const handleShowUploadFile = (hardwareIndex, field) => {
    setSelectedHardwareIndex(hardwareIndex);
    setSelectedField(field);
    setShowFileUploadModal(true);
  };

  const handleShowDeleteFile = (hardwareIndex, field) => {
    setSelectedHardwareIndex(hardwareIndex);
    setSelectedField(field);
    setIsShowDeleteAsset(LABEL_DELETE_ASSET);
  };

  const handleCancelUploadFile = () => {
    setShowFileUploadModal(false);
    setSelectedHardwareIndex(null);
    setSelectedField(null);
  };

  const addGroup = () => {
    dispatch(setValueHardware({
      value: {
        ...hardwareData,
        items: [...hardwareItems, INITIAL_HARDWARE_ITEM]
      }
    }));
  };

  const deleteGroup = (index) => {
    if (hardwareItems.length <= 1) {
      alert(PLACEHOLDER_CONFIG.retainHardware[language]);
      return;
    }
    setHardwareToDelete(index);
    setShowDeleteHardwareModal(true);
  };

  const handleConfirmDeleteHardware = () => {
    if (hardwareToDelete !== null) {
      const newItems = hardwareItems.filter((_, i) => i !== hardwareToDelete);
      dispatch(setValueHardware({
        value: {
          ...hardwareData,
          items: newItems
        }
      }));
    }
    setShowDeleteHardwareModal(false);
    setHardwareToDelete(null);
  };

  const updateHardware = (field, value) => {
    dispatch(setValueHardware({
      value: { ...hardwareData, [field]: value }
    }));
  };

  const updateHardwareItem = (index, field, value) => {
    dispatch(setValueHardware({
      value: {
        ...hardwareData,
        items: hardwareItems.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const validateAllHardware = () => ({});

  useImperativeHandle(ref, () => ({
    addGroup,
    validateAllHardware
  }));

  const renderFileUploadSection = (item, index) => (
    <div className='package-input'>
      <label>Image:</label>
      <input
        type="text"
        placeholder={PLACEHOLDER_CONFIG.imageUrl[language]}
        value={item.imageUrl || ''}
        disabled
        style={{ width: '73%' }}
      />
      {!item.imageUrl ? (
        <button
          className="upload-btn"
          onClick={() => handleShowUploadFile(index, "imageUrl")}
        >
          Upload
        </button>
      ) : (
        <button
          className="icon-btn"
          onClick={() => handleShowDeleteFile(index, "imageUrl")}
        >
          <img src={DeleteIcon} alt="Delete" className="action-icon" />
        </button>
      )}
    </div>
  );

  const renderHardwareItem = (item, index) => (
    <div key={index}>
      <div className="domain-group">
        <div className='package-input'>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label>Hardware Title:</label>
          </div>
          <CustomInput
            type="text"
            value={item.title || ''}
            onChange={(e) => updateHardwareItem(index, 'title', e.target.value)}
            placeholder={PLACEHOLDER_CONFIG.hardwareTitle[language]}
            customStyle={{ width: '73%' }}
          />
          <button
            className="icon-btn"
            onClick={() => deleteGroup(index)}
          >
            <img src={DeleteIcon} alt="Delete" className="campaign-delete-icon" />
          </button>
        </div>

        <div className='package-input'>
          <label>Description:</label>
          <textarea
            type="text"
            value={item.desc || ''}
            onChange={(e) => updateHardwareItem(index, 'desc', e.target.value)}
            placeholder={PLACEHOLDER_CONFIG.hardwareDescription[language]}
            customStyle={{ width: '73%' }}
          />
        </div>

        {renderFileUploadSection(item, index)}

        {item.imageUrl && (
          <div style={{ marginLeft: '190px', marginBottom: '20px' }}>
            <img
              src={`https://uat.stack-ez.com${item.imageUrl}`}
              alt="Preview"
              style={{ maxWidth: '200px', maxHeight: '200px', marginTop: '10px' }}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: '20px', width: '35%' }}>
          <div className='package-input'>
            <label>Width:</label>
            <input
              type="number"
              value={item.width === '' ? '' : (item.width || 275)}
              onChange={(e) => updateHardwareItem(index, 'width', e.target.value === '' ? '' : parseInt(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '20px', width: '35%' }}>
          <div className='package-input'>
            <label>Height:</label>
            <input
              type="number"
              value={item.height === '' ? '' : (item.height || 275)}
              onChange={(e) => updateHardwareItem(index, 'height', e.target.value === '' ? '' : parseInt(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </div>

      {index !== hardwareItems.length - 1 && (
        <div
          style={{
            height: '0.5px',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            margin: '25px -40px',
            width: 'calc(100% + 80px)',
            display: 'block',
            boxSizing: 'border-box'
          }}
        />
      )}
    </div>
  );

  return (
    <div className="tab-package-container">
      <div className="domain-group">
        <div className='package-input'>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label>Title:</label>
          </div>
          <CustomInput
            type="text"
            value={hardwareTitle}
            onChange={(e) => updateHardware('title', e.target.value)}
            placeholder={PLACEHOLDER_CONFIG.title[language]}
            customStyle={{ width: '73%' }}
          />
        </div>
      </div>

      <div
        style={{
          height: '0.5px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          margin: '25px -40px',
          width: 'calc(100% + 80px)',
          display: 'block',
          boxSizing: 'border-box'
        }}
      />

      {hardwareItems.map(renderHardwareItem)}

      <ModalFileUpload
        show={showFileUploadModal}
        handleCancel={handleCancelUploadFile}
        handleUpload={handleFileUpload}
        isLoading={isUploading}
      />

      <CommonDeleteModal
        show={!isEmpty(isShowDeleteAsset)}
        onHide={() => setIsShowDeleteAsset(null)}
        onConfirm={handleDeleteFile}
      />

      <CommonDeleteModal
        show={showDeleteHardwareModal}
        onHide={() => {
          setShowDeleteHardwareModal(false);
          setHardwareToDelete(null);
        }}
        onConfirm={handleConfirmDeleteHardware}
        title="Delete Hardware Item"
        instruction="Are you sure you want to delete this hardware item?"
      />
    </div>
  );
});

export default Hardware;